import { omit } from 'lodash-es'
import { action, observable } from 'mobx'
import {
  type IValidationSchema,
  type IValidator,
  required,
  validationContext,
} from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import { generateIdFromText } from '@/features/legends/editor/entity-ids'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import type { EffectEditorViewModel } from '@/features/legends/effects/EffectEditorViewModel'
import type { FiltersBuilderViewModel } from '@/features/legends/filters/FiltersBuilderViewModel'
import type { SelectOption } from '@/features/legends/questions/questions'
import { validateObject } from '@/utils/validx-util'
import type { SelectQuestionEditorViewModel } from './SelectQuestionEditorViewModel'

const uniqueAmongOptions: IValidator<SelectOptionEditorViewModel> = ({
  obj,
  value,
}) => obj.isUniqueId(String(value ?? '')) || 'Duplicate ID.'

/**
 * An option's ID. Older legends have numeric ones, which are kept as numbers
 * until the ID is edited, so the values filters compare against still match.
 */
export type SelectOptionId = string | number

/**
 * An option of a select question, with its own effect and conditions.
 */
export class SelectOptionEditorViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<SelectOptionEditorViewModel> = {
    id: [required(), uniqueAmongOptions],
    text: [required('Text is required')],
    effect: [validateObject('Invalid effect')],
    triggerWhen: [validateObject('Invalid conditions')],
  }

  /**
   * Identifies the option on the client, for keys.
   */
  readonly cid = newCid('select-option')

  /**
   * The question the option belongs to.
   */
  readonly question: SelectQuestionEditorViewModel

  /**
   * Runs when the option is picked.
   */
  readonly effect: EffectEditorViewModel

  /**
   * When the option is offered.
   */
  readonly triggerWhen: FiltersBuilderViewModel

  /**
   * The value formulas and filters see when the option is picked.
   */
  @observable accessor id: SelectOptionId = ''

  /**
   * The ID as typed. It becomes the ID once committed, if it is valid.
   */
  @observable accessor idDraft = ''
  @observable accessor text = ''

  readonly validation = validationContext(this, this._schema)

  constructor(
    dependencies: LegendPartDependencies,
    question: SelectQuestionEditorViewModel,
  ) {
    super(dependencies)
    this.question = question
    this.effect = question.createEffect()
    this.triggerWhen = question.createFiltersBuilder()
    this.lifecycle.manage(this.effect)
    this.lifecycle.manage(this.triggerWhen)
    // The ID is validated when it is committed.
    this.lifecycle.manage(validateWhileEditing(this, omit(this._schema, 'id')))
  }

  /**
   * Whether no other option of the question has the ID, ignoring case.
   */
  isUniqueId(id: string) {
    const upped = id.toUpperCase()
    return !this.question.options.some(
      (o) => o !== this && String(o.id).toUpperCase() === upped,
    )
  }

  /**
   * Sets the ID to the draft, when it is present and unique among the
   * options. Setting the current ID again clears the error of an earlier
   * attempt.
   */
  @action.bound
  commitId() {
    this.validation.clearErrors('id')
    if (this.idDraft === String(this.id)) {
      return
    }
    const error = !this.idDraft
      ? 'This field is required'
      : !this.isUniqueId(this.idDraft) && 'Duplicate ID.'
    if (error) {
      this.validation.addErrors({ id: [error] })
      return
    }
    this.id = this.idDraft
  }

  /**
   * Gives the option an ID from its text, until it has one. A duplicate is
   * left in the ID field with its error, like a typed one.
   */
  @action.bound
  deriveIdFromText() {
    if (this.id === '') {
      this.idDraft = generateIdFromText('OPT', this.text)
      this.commitId()
    }
  }

  /**
   * Sets the option from the legend document.
   */
  @action
  hydrate(option: SelectOption) {
    this.effect.hydrate(option.effect)
    this.triggerWhen.hydrate(option.triggerWhen)
    this.id = option.id ?? ''
    this.idDraft = String(this.id)
    this.text = option.text ?? ''
  }

  /**
   * Shows the analyzer's errors for the option and its effect, which share
   * the option's object in the legend document.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    const { id, text, triggerWhen, ...effectDescriptor } =
      descriptor.inner ?? {}
    receiveErrors(this, { id, text, triggerWhen })
    this.effect.receiveErrors({ errors: [], inner: effectDescriptor })
  }

  /**
   * The option as the legend document stores it.
   */
  toJSON(): SelectOption {
    return {
      id: this.id,
      text: this.text,
      triggerWhen: this.triggerWhen.toJSON(),
      effect: this.effect.toJSON(),
    }
  }
}
